import './ClientProfitabilitySurvey.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Page, PageNav } from '@Components/Page';
import { PAGE_STATE } from '@Components/Page/page-constants';
import { CpsCurrentUserResponse, CpsPermission, CpsTab, CpsTabType } from './cps-utils';
import { SurveyTab } from './SurveyTab';
import { ReportsTab } from './ReportsTab';
import { AdminTab } from './AdminTab';
import { awaitJSON, ClientToolsLambdaFetch } from '../../utils/fetch-utils';
import { useSetError } from '../../redux/modals';

const ClientProfitabilitySurvey = () => {
  const setError = useSetError();
  const [cpsUser, setCpsUser] = useState<CpsCurrentUserResponse | null>(null);
  const [surveyDate, setSurveyDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<CpsTabType>(CpsTab.SURVEY);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [userRes, dateRes] = await Promise.all([
          ClientToolsLambdaFetch('/cps/currentUser'),
          ClientToolsLambdaFetch('/cps/nextSurveyDate')
        ]);
        const [userData, dateData] = await Promise.all([
          awaitJSON<CpsCurrentUserResponse>(userRes),
          awaitJSON<string>(dateRes)
        ]);
        setCpsUser(userData);
        setSurveyDate(dateData);
      } catch (e) {
        setError({
          message: `Failed to load CPS data: ${e.message}`,
          reportError: e
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [setError]);

  const permission = cpsUser?.permission;

  const navs = useMemo<PageNav[]>(() => {
    const tabs: PageNav[] = [{ label: 'Survey', key: CpsTab.SURVEY }];
    if (
      permission === CpsPermission.MANAGER ||
      permission === CpsPermission.FINANCE ||
      permission === CpsPermission.ADMIN
    ) {
      tabs.push({ label: 'Reports', key: CpsTab.REPORTS });
    }
    if (permission === CpsPermission.FINANCE || permission === CpsPermission.ADMIN) {
      tabs.push({ label: 'Admin', key: CpsTab.ADMIN });
    }
    return tabs;
  }, [permission]);

  const handleNav = useCallback((key: string) => setActiveTab(key as CpsTabType), []);

  if (loading) {
    return (
      <Page
        title='Client Profitability Survey'
        pageType='Client Profitability Survey'
        state={PAGE_STATE.LOADING}
      />
    );
  }

  if (!cpsUser || !surveyDate) {
    return (
      <Page title='Client Profitability Survey' pageType='Client Profitability Survey'>
        <p>Unable to load CPS data. Please try again later.</p>
      </Page>
    );
  }

  return (
    <Page
      title='Client Profitability Survey'
      pageType='Client Profitability Survey'
      navs={navs}
      selectedNav={activeTab}
      onNav={handleNav}
    >
      <div className='client-profitability-survey'>
        {activeTab === CpsTab.SURVEY && (
          <SurveyTab cpsUser={cpsUser} surveyDate={surveyDate} surveyClosed={false} />
        )}
        {activeTab === CpsTab.REPORTS && <ReportsTab cpsUserName={cpsUser.name} />}
        {activeTab === CpsTab.ADMIN && <AdminTab />}
      </div>
    </Page>
  );
};

export { ClientProfitabilitySurvey };
